import parseUrl from 'parse-url';
import React, { Fragment } from 'react';
import { formatPhoneNumberForLink, getOpeningHours } from '../../../utils/Card';
import { type Card, getStaticMapUrl } from '../../../utils/CardDetails';
import UserAddedSpan from '../UserAddedSpan';
import './CardDetailsInfo.scss';

const GOOGLE_MAPS_ID = '5f2483670fd46e10';

const MOBILE_MAP_OPTIONS = {
  zoom: 15,
  size: { width: 350, height: 350 },
  scale: 4,
  mapId: GOOGLE_MAPS_ID,
  markerLabel: 'o',
} as const;

interface CardDetailsInfoProps {
  card: Card;
  isMobile: boolean;
}

function CardDetailsInfo({ card, isMobile }: CardDetailsInfoProps) {
  const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'short' });
  const mobileMapUrl = isMobile ? getStaticMapUrl(card, MOBILE_MAP_OPTIONS) : '';
  const infoContainerClassName = isMobile ? 'mobile-info-container' : 'info-container';

  return (
    <div className={infoContainerClassName}>
      <div className='contact-info-container'>
        <PhoneSection card={card} />
        {!isMobile && <AddressSection card={card} />}
        <WebsiteSection
          card={card}
          isMobile={isMobile}
        />
      </div>

      <OpeningHoursSection
        card={card}
        currentDay={currentDay}
        isMobile={isMobile}
      />

      {isMobile && (
        <AddressSection
          card={card}
          mapUrl={mobileMapUrl}
        />
      )}

      {isMobile && card.creator && (
        <UserAddedSpan
          card={card}
          isMobile
        />
      )}
    </div>
  );
}

function PhoneSection({ card }: { card: Card }) {
  if (!card.formattedPhoneNumber) {
    return null;
  }

  return (
    <div>
      <h4 className='info-heading'>Phone</h4>
      <span className='info-span'>
        {card.internationalPhoneNumber ? (
          <a
            className='styled-anchor'
            href={`tel:${formatPhoneNumberForLink(card.internationalPhoneNumber)}`}
          >
            {card.internationalPhoneNumber}
          </a>
        ) : (
          card.formattedPhoneNumber
        )}
      </span>
    </div>
  );
}

function AddressSection({ card, mapUrl }: { card: Card; mapUrl?: string }) {
  if (!card.formattedAddress) {
    return null;
  }

  return (
    <div>
      <h4 className='info-heading'>Address</h4>
      <span className='info-span'>
        {card.url ? (
          <a
            className='styled-anchor'
            href={card.url}
            target='_blank'
            rel='noreferrer'
          >
            {card.formattedAddress}
          </a>
        ) : (
          card.formattedAddress
        )}
      </span>
      {mapUrl && (
        <div className='static-map-container'>
          <a
            href={card.url}
            target='_blank'
            rel='noreferrer'
          >
            <img
              className='static-map'
              src={mapUrl}
              alt=''
            />
          </a>
        </div>
      )}
    </div>
  );
}

function WebsiteSection({ card, isMobile }: { card: Card; isMobile: boolean }) {
  if (!card.website) {
    return null;
  }

  return (
    <div>
      <h4 className='info-heading'>Website</h4>
      <span className='info-span'>
        <a
          className={`styled-anchor${isMobile ? '' : ' website'}`}
          href={card.website}
          target='_blank'
          rel='noreferrer'
        >
          {isMobile ? card.website : parseUrl(card.website).resource}
        </a>
      </span>
    </div>
  );
}

function OpeningHoursSection({
  card,
  currentDay,
  isMobile,
}: {
  card: Card;
  currentDay: string;
  isMobile: boolean;
}) {
  if (!card.openingHours) return null;

  return (
    <div className={isMobile ? undefined : 'hours-info-container'}>
      <h4 className='info-heading'>Hours</h4>
      <div className='hours-info-subcontainer'>
        {getOpeningHours(card.openingHours).map((openingHours, dayIndex) => {
          const dayOfWeek = Object.keys(openingHours)[0];
          const isToday = dayOfWeek === currentDay;
          const fontWeight = isToday ? '600' : 'normal';

          return (
            <Fragment key={`${card.id}-${dayOfWeek}`}>
              <span
                className={isMobile ? 'info-span' : 'info-grid-span'}
                style={{ gridRow: dayIndex + 1, fontWeight }}
              >
                {dayOfWeek}:
              </span>
              <div
                className='time-info-container'
                style={{ gridRow: dayIndex + 1 }}
              >
                {openingHours[dayOfWeek].map((hours: string, index: number) => (
                  <span
                    key={`${card.id}-hours-${dayOfWeek}-${index}`}
                    className='info-span'
                    style={{ fontWeight }}
                  >
                    {hours}
                  </span>
                ))}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

CardDetailsInfo.displayName = 'CardDetailsInfo';
export default CardDetailsInfo;
